/**
 * Soulbotanic visitor beacon.
 *
 * Reports each visit to the analytics Worker: who the device is, where the
 * connection comes from, how it arrived, which pages it opened and how long it
 * actually stayed on each one. Configuration arrives on `window.sbVisitorsConfig`
 * from snippets/sb-visitor-beacon.liquid.
 *
 * Privacy posture (decided 2026-09-07): every hit is recorded, but the
 * PERSISTENT cross-visit id is only written once Shopify's Customer Privacy API
 * reports `analytics` consent. Without consent a visitor is still measured —
 * just as a one-off session, with no identity carried between visits.
 *
 * Public helper: window.sbTrack('name', { any: 'data' })
 */
(function () {
  'use strict';

  var cfg = window.sbVisitorsConfig || {};
  var ENDPOINT = cfg.endpoint;
  if (!ENDPOINT || navigator.doNotTrack === 'not-supported') return;
  if (window.__sbVisitors) return;
  window.__sbVisitors = true;

  var SESSION_KEY = 'sb.vs';
  var VISITOR_KEY = 'sb.vv';
  var SESSION_GAP = 30 * 60 * 1000; // a 30-minute gap starts a new session
  var HEARTBEATS = [10000, 20000, 40000, 60000]; // then every 60s
  var IDLE_AFTER = 30000; // no input for 30s → the clock stops counting "engaged"

  /* ------------------------------------------------------------- storage */
  function read(store, key) {
    try { return JSON.parse(window[store].getItem(key)); } catch (e) { return null; }
  }
  function write(store, key, value) {
    try { window[store].setItem(key, JSON.stringify(value)); return true; } catch (e) { return false; }
  }
  var rid = function () {
    try {
      return crypto.randomUUID().replace(/-/g, '').slice(0, 24);
    } catch (e) {
      return (Date.now().toString(36) + Math.random().toString(36).slice(2, 12)).slice(0, 24);
    }
  };

  /* ------------------------------------------------------------- consent */
  function consentState() {
    try {
      var api = window.Shopify && window.Shopify.customerPrivacy;
      if (!api || typeof api.currentVisitorConsent !== 'function') return null;
      var c = api.currentVisitorConsent();
      return { analytics: c.analytics === 'yes', marketing: c.marketing === 'yes', preferences: c.preferences === 'yes' };
    } catch (e) { return null; }
  }
  var hasAnalyticsConsent = function () { var c = consentState(); return !!(c && c.analytics); };

  /** The persistent id exists only under consent; otherwise the server falls
   *  back to the device fingerprint, which is not stored on the visitor. */
  function visitorId() {
    if (!hasAnalyticsConsent()) return null;
    var v = read('localStorage', VISITOR_KEY);
    if (v && v.id) return v.id;
    var id = rid();
    write('localStorage', VISITOR_KEY, { id: id, born: Date.now() });
    return id;
  }

  /* ------------------------------------------------------------- session */
  var now = Date.now();
  var s = read('sessionStorage', SESSION_KEY);
  if (!s || !s.id || now - (s.last || 0) > SESSION_GAP) {
    s = { id: rid(), n: 0, started: now, last: now, t: [0, 0, 0, 0, 0, 0, 0], fp: null };
  }
  s.n += 1;
  s.last = now;
  var SESSION = s.id;
  var SEQ = s.n;
  var VIEW = rid();
  // Session totals: [duration, active, maxScroll, clicks, moves, keys, scrolls]
  var TOT = s.t || [0, 0, 0, 0, 0, 0, 0];
  var baseDuration = TOT[0];
  var baseActive = TOT[1];

  /* --------------------------------------------------------- fingerprint */
  /** FNV-1a — fast, dependency-free, and we only need a stable short digest. */
  function hash(str) {
    var h = 2166136261;
    for (var i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = (h * 16777619) >>> 0; }
    return h.toString(16).padStart(8, '0');
  }

  function canvasHash() {
    try {
      var c = document.createElement('canvas');
      c.width = 240; c.height = 60;
      var x = c.getContext('2d');
      if (!x) return null;
      x.textBaseline = 'top';
      x.font = '14px "Arial"';
      x.fillStyle = '#f60'; x.fillRect(120, 1, 62, 20);
      x.fillStyle = '#069'; x.fillText('Soulbotanic \u{1F33F} fp', 2, 15);
      x.fillStyle = 'rgba(102,204,0,0.7)'; x.fillText('Soulbotanic \u{1F33F} fp', 4, 17);
      x.globalCompositeOperation = 'multiply';
      x.beginPath(); x.arc(50, 50, 40, 0, Math.PI * 2, true); x.fillStyle = 'rgb(255,0,255)'; x.fill();
      return hash(c.toDataURL());
    } catch (e) { return null; }
  }

  function webgl() {
    try {
      var c = document.createElement('canvas');
      var gl = c.getContext('webgl') || c.getContext('experimental-webgl');
      if (!gl) return { ok: false };
      var dbg = gl.getExtension('WEBGL_debug_renderer_info');
      var parts = [];
      [gl.VERSION, gl.SHADING_LANGUAGE_VERSION, gl.VENDOR, gl.RENDERER,
        gl.MAX_TEXTURE_SIZE, gl.MAX_VIEWPORT_DIMS, gl.MAX_RENDERBUFFER_SIZE,
        gl.MAX_VERTEX_ATTRIBS, gl.MAX_VARYING_VECTORS, gl.ALIASED_LINE_WIDTH_RANGE,
      ].forEach(function (p) { try { parts.push(String(gl.getParameter(p))); } catch (e) { /* ignore */ } });
      try { parts.push((gl.getSupportedExtensions() || []).join(',')); } catch (e) { /* ignore */ }
      return {
        ok: true,
        vendor: dbg ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR),
        renderer: dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
        hash: hash(parts.join('|')),
      };
    } catch (e) { return { ok: false }; }
  }

  var FONT_PROBE = ['Arial', 'Helvetica Neue', 'Times New Roman', 'Georgia', 'Courier New',
    'Verdana', 'Tahoma', 'Trebuchet MS', 'Impact', 'Comic Sans MS', 'Palatino', 'Garamond',
    'Bookman', 'Candara', 'Segoe UI', 'Menlo', 'Monaco', 'Consolas', 'Roboto', 'Ubuntu',
    'Cantarell', 'DejaVu Sans', 'Liberation Sans', 'Optima', 'Futura', 'Gill Sans'];

  /** Which of the probe fonts are actually installed, by width comparison. */
  function fonts() {
    try {
      var base = ['monospace', 'sans-serif', 'serif'];
      var span = document.createElement('span');
      span.style.cssText = 'position:absolute;left:-9999px;top:-9999px;font-size:72px;white-space:nowrap';
      span.textContent = 'mmmmmmmmmmlli';
      document.body.appendChild(span);
      var ref = {};
      base.forEach(function (b) { span.style.fontFamily = b; ref[b] = [span.offsetWidth, span.offsetHeight]; });
      var found = [];
      FONT_PROBE.forEach(function (f) {
        for (var i = 0; i < base.length; i++) {
          span.style.fontFamily = '"' + f + '",' + base[i];
          if (span.offsetWidth !== ref[base[i]][0] || span.offsetHeight !== ref[base[i]][1]) { found.push(f); return; }
        }
      });
      document.body.removeChild(span);
      return found;
    } catch (e) { return []; }
  }

  /** OfflineAudioContext digest. Strong, but flaky — recorded, never used for identity. */
  function audioHash() {
    return new Promise(function (resolve) {
      try {
        var Ctx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        if (!Ctx) return resolve(null);
        var ctx = new Ctx(1, 5000, 44100);
        var osc = ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.value = 10000;
        var comp = ctx.createDynamicsCompressor();
        comp.threshold.value = -50; comp.knee.value = 40; comp.ratio.value = 12;
        comp.attack.value = 0; comp.release.value = 0.25;
        osc.connect(comp); comp.connect(ctx.destination);
        osc.start(0);
        ctx.oncomplete = function (e) {
          try {
            var d = e.renderedBuffer.getChannelData(0);
            var sum = 0;
            for (var i = 4500; i < 5000; i++) sum += Math.abs(d[i]);
            resolve(hash(String(sum)));
          } catch (err) { resolve(null); }
        };
        ctx.startRendering();
      } catch (e) { resolve(null); }
    });
  }

  /** The headless tell: a denied Notification permission that still reports "prompt". */
  function permissionMismatch() {
    return new Promise(function (resolve) {
      try {
        if (!navigator.permissions || !window.Notification) return resolve(false);
        navigator.permissions.query({ name: 'notifications' }).then(function (p) {
          resolve(Notification.permission === 'denied' && p.state === 'prompt');
        }).catch(function () { resolve(false); });
      } catch (e) { resolve(false); }
    });
  }

  /** Private browsing leaves a much smaller storage quota than a normal profile. */
  function incognito() {
    return new Promise(function (resolve) {
      try {
        if (!navigator.storage || !navigator.storage.estimate) return resolve(null);
        navigator.storage.estimate().then(function (e) {
          resolve(typeof e.quota === 'number' ? (e.quota < 200 * 1024 * 1024 ? 1 : 0) : null);
        }).catch(function () { resolve(null); });
      } catch (e) { resolve(null); }
    });
  }

  function collectFingerprint() {
    var gl = webgl();
    var nav = navigator;
    var base = {
      sw: screen.width, sh: screen.height, cd: screen.colorDepth,
      vw: window.innerWidth, vh: window.innerHeight, ow: window.outerWidth,
      dpr: window.devicePixelRatio || 1,
      touch: nav.maxTouchPoints || 0,
      cpu: nav.hardwareConcurrency || null,
      mem: nav.deviceMemory || null,
      lang: nav.language, langs: (nav.languages || []).slice(0, 8),
      tz: (function () { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) { return null; } })(),
      tzo: new Date().getTimezoneOffset(),
      plat: (nav.userAgentData && nav.userAgentData.platform) || nav.platform || '',
      brands: nav.userAgentData ? (nav.userAgentData.brands || []).map(function (b) { return b.brand; }).join(',') : '',
      cvs: canvasHash(),
      wgl: gl.ok, wglh: gl.hash || null, wgv: gl.vendor || null, wgr: gl.renderer || null,
      fonts: fonts(),
      ck: nav.cookieEnabled,
      ls: (function () { try { localStorage.setItem('sb.t', '1'); localStorage.removeItem('sb.t'); return true; } catch (e) { return false; } })(),
      dnt: nav.doNotTrack || window.doNotTrack || null,
      wd: nav.webdriver === true,
      plugins: nav.plugins ? nav.plugins.length : 0,
      chromeObj: !!window.chrome,
      // Brave reports itself as Chrome in the UA string on purpose. It does
      // expose navigator.brave, so we can name it honestly rather than
      // recording every Brave user as a Chrome user.
      brave: !!nav.brave,
    };

    // The async probes get a hard 350ms budget — a fingerprint is never worth
    // delaying the first beacon past a fast bounce.
    return Promise.race([
      Promise.all([audioHash(), permissionMismatch(), incognito()]),
      new Promise(function (r) { setTimeout(function () { r([null, false, null]); }, 350); }),
    ]).then(function (extra) {
      base.aud = extra[0];
      base.permMismatch = extra[1];
      base.priv = extra[2];
      return base;
    });
  }

  /* ---------------------------------------------------------- behaviour */
  var started = Date.now();
  var lastInput = Date.now();
  var activeMs = 0;
  var lastTick = Date.now();
  var maxScroll = 0;
  var clicks = 0, moves = 0, keys = 0, scrolls = 0;
  var moveThrottle = 0;

  function tick() {
    var t = Date.now();
    // Count the interval only when the tab was visible AND the visitor has done
    // something recently. That is what makes "engaged time" mean anything.
    if (document.visibilityState === 'visible' && t - lastInput < IDLE_AFTER) activeMs += t - lastTick;
    lastTick = t;
  }
  setInterval(tick, 1000);

  function input(kind) {
    lastInput = Date.now();
    if (kind === 'click') clicks++;
    else if (kind === 'key') keys++;
    else if (kind === 'scroll') scrolls++;
    else moves++;
  }
  addEventListener('click', function () { input('click'); }, { passive: true, capture: true });
  addEventListener('keydown', function () { input('key'); }, { passive: true });
  addEventListener('pointermove', function () {
    var t = Date.now();
    if (t - moveThrottle < 250) { lastInput = t; return; }
    moveThrottle = t; input('move');
  }, { passive: true });
  addEventListener('touchstart', function () { input('move'); }, { passive: true });
  addEventListener('scroll', function () {
    input('scroll');
    var h = document.documentElement.scrollHeight - window.innerHeight;
    var pct = h > 0 ? Math.round((window.scrollY / h) * 100) : 100;
    if (pct > maxScroll) maxScroll = Math.min(100, pct);
  }, { passive: true });

  /* ----------------------------------------------------------- sections */
  // Which parts of the page were actually reached, how long each one held the
  // viewport, and which one was on screen when the visitor left. Shopify wraps
  // every section in .shopify-section with an id that ends in the section's
  // key; that key (minus its random suffix) is the name the dashboard shows.
  var SEC = {};        // name -> [reachOrder, visibleMs, maxVisiblePct]
  var secSince = {};   // name -> when it became visible (0 = not visible)
  var secArea = {};    // name -> visible height in px right now (0 = off screen)
  var secPaused = [];
  var secCount = 0;
  function sectionName(el) {
    var n = el.getAttribute('data-sb-section') || el.id || '';
    n = n.replace(/^shopify-section-/, '').replace(/^(template|sections)--[^_]+__/, '');
    n = n.replace(/_[A-Za-z0-9]{6,8}$/, '');
    return n.slice(0, 48) || null;
  }
  function secFlush(now) {
    for (var k in secSince) { if (secSince[k]) { SEC[k][1] += now - secSince[k]; secSince[k] = now; } }
  }
  function secPause() {
    var now = Date.now(); secFlush(now); secPaused = [];
    for (var k in secSince) { if (secSince[k]) { secPaused.push(k); secSince[k] = 0; } }
  }
  function secResume() {
    var now = Date.now();
    secPaused.forEach(function (k) { secSince[k] = now; });
    secPaused = [];
  }
  // "The section they are reading" = the one covering most of the viewport.
  // Ratios would crown the fixed header (always 100% visible); pixels do not.
  function currentSection() {
    var best = null, bestArea = 0;
    for (var k in secArea) { if (secArea[k] > bestArea) { bestArea = secArea[k]; best = k; } }
    return best;
  }
  function sectionsPayload() {
    if (document.visibilityState === 'visible') secFlush(Date.now());
    var list = [];
    for (var k in SEC) list.push([k, Math.round(SEC[k][1]), SEC[k][0], SEC[k][2]]);
    return list.length ? list : null;
  }
  try {
    if (window.IntersectionObserver) {
      var secNodes = document.querySelectorAll('.shopify-section, [data-sb-section]');
      if (secNodes.length && secNodes.length <= 80) {
        var secObserver = new IntersectionObserver(function (entries) {
          var now = Date.now();
          entries.forEach(function (en) {
            var name = sectionName(en.target);
            if (!name) return;
            if (en.isIntersecting) {
              // The observer's first callback reports every element, on screen
              // or not — only an actual intersection counts as "reached".
              if (!SEC[name]) SEC[name] = [++secCount, 0, 0];
              var pct = Math.round(en.intersectionRatio * 100);
              if (!secSince[name]) secSince[name] = now;
              if (pct > SEC[name][2]) SEC[name][2] = pct;
              secArea[name] = en.intersectionRect ? en.intersectionRect.height : en.intersectionRatio;
            } else {
              if (secSince[name]) { SEC[name][1] += now - secSince[name]; secSince[name] = 0; }
              secArea[name] = 0;
            }
          });
        }, { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] });
        secNodes.forEach(function (el) { secObserver.observe(el); });
        addEventListener('visibilitychange', function () {
          if (document.visibilityState === 'hidden') secPause(); else secResume();
        });
      }
    }
  } catch (e) { /* the page is still measured, just not by section */ }

  function viewMetrics() { tick(); return [Date.now() - started, activeMs, maxScroll, clicks]; }
  function sessionMetrics() {
    tick();
    return [
      baseDuration + (Date.now() - started), baseActive + activeMs,
      Math.max(TOT[2], maxScroll), TOT[3] + clicks, TOT[4] + moves, TOT[5] + keys, TOT[6] + scrolls,
    ];
  }
  function persist() {
    var m = sessionMetrics();
    write('sessionStorage', SESSION_KEY, { id: SESSION, n: SEQ, started: s.started, last: Date.now(), t: m, fp: s.fp });
  }

  /* ------------------------------------------------------------- sending */
  function send(payload, urgent) {
    payload.s = SESSION;
    payload.vw = VIEW;
    var body = JSON.stringify(payload);
    try {
      // text/plain keeps this a CORS-simple request: no preflight, and it still
      // works from the unload handler.
      if (urgent && navigator.sendBeacon) {
        var blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
        if (navigator.sendBeacon(ENDPOINT, blob)) return;
      }
      fetch(ENDPOINT, {
        method: 'POST', body: body, keepalive: true, mode: 'cors', credentials: 'omit',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      }).catch(function () { /* a lost beacon is not the visitor's problem */ });
    } catch (e) { /* ignore */ }
  }

  /** Shopify's own visitor cookie — the key a Web Pixel would use to join a
   *  checkout it sees on Shopify's domain back to this session. */
  function shopifyY() {
    try {
      var m = /(?:^|; )_shopify_y=([^;]+)/.exec(document.cookie || '');
      return m ? decodeURIComponent(m[1]).slice(0, 64) : null;
    } catch (e) { return null; }
  }

  function shopContext() {
    var ctx = {};
    try {
      ctx.cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || cfg.currency || null;
      ctx.loc = (window.Shopify && Shopify.locale) || cfg.locale || null;
      ctx.cid = cfg.customerId || null;
      if (typeof cfg.cartValue === 'number') ctx.cart = cfg.cartValue;
    } catch (e) { /* ignore */ }
    return ctx;
  }

  var sentHit = false;
  function sendHit(fingerprint) {
    if (sentHit) return;
    sentHit = true;
    send({
      t: 'hit',
      v: visitorId(),
      n: SEQ,
      u: location.href,
      r: document.referrer || null,
      ti: document.title,
      pt: cfg.pageType || null,
      c: SEQ === 1 || !s.fp ? fingerprint : undefined,
      cn: consentState(),
      sh: shopContext(),
      sy: shopifyY(),
      m: { v: viewMetrics(), s: sessionMetrics() },
      ua: navigator.userAgent,
    });
    s.fp = 1;
    persist();
  }

  // Fire the first hit as soon as the fingerprint settles; if the visitor leaves
  // before that, flush immediately with whatever we have.
  var fpPromise = collectFingerprint();
  fpPromise.then(sendHit);
  addEventListener('pagehide', function () { if (!sentHit) sendHit({}); }, { once: true, capture: true });

  /* ---------------------------------------------------------- heartbeats */
  var beat = 0;
  function schedule() {
    var wait = HEARTBEATS[Math.min(beat, HEARTBEATS.length - 1)];
    setTimeout(function () {
      beat++;
      if (sentHit && document.visibilityState === 'visible') {
        send({ t: 'hb', p: location.pathname, m: { v: viewMetrics(), s: sessionMetrics() }, sc: sectionsPayload(), xs: currentSection(), sy: shopifyY() });
      }
      persist();
      schedule();
    }, wait);
  }
  schedule();

  /* ------------------------------------------------------------ the exit */
  var ended = false;
  function end() {
    if (ended) return;
    ended = true;
    persist();
    send({ t: 'end', p: location.pathname, m: { v: viewMetrics(), s: sessionMetrics() }, sc: sectionsPayload(), xs: currentSection(), sy: shopifyY() }, true);
  }
  addEventListener('pagehide', end, { capture: true });
  addEventListener('beforeunload', end, { capture: true });
  addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') {
      persist();
      send({ t: 'end', p: location.pathname, m: { v: viewMetrics(), s: sessionMetrics() }, sc: sectionsPayload(), xs: currentSection(), sy: shopifyY() }, true);
      ended = false; // the visitor may come back to this same page
    }
  });

  /* ------------------------------------------------------------- events */
  function track(name, data) {
    send({ t: 'ev', p: location.pathname, e: { n: String(name).slice(0, 64), d: data || null } });
  }
  window.sbTrack = track;

  // Cart adds. The theme posts to /cart/add.js through fetch, and older paths
  // still submit a real form — cover both without owning either.
  document.addEventListener('submit', function (e) {
    try {
      var action = (e.target && e.target.getAttribute('action')) || '';
      if (/\/cart\/add/.test(action)) track('add_to_cart', { form: true });
    } catch (err) { /* ignore */ }
  }, true);

  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) { /* ignore */ }
      if (/\/cart\/add(\.js)?/.test(url)) track('add_to_cart', { path: location.pathname });
      else if (/\/cart\/change/.test(url)) track('cart_change', null);
      return nativeFetch.apply(this, arguments);
    };
  }

  // Anything that leads to Shopify checkout is the moment worth knowing about.
  document.addEventListener('click', function (e) {
    try {
      var el = e.target.closest('a[href*="/checkout"], button[name="checkout"], form[action*="/checkout"] button[type="submit"]');
      if (el) track('checkout_started', { cart: shopContext().cart || null });
    } catch (err) { /* ignore */ }
  }, true);

  // Shopify's own analytics events, when the storefront emits them.
  try {
    if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.subscribe === 'function') {
      ['product_added_to_cart', 'checkout_started', 'checkout_completed'].forEach(function (name) {
        Shopify.analytics.subscribe(name, function (ev) {
          track(name, { total: ev && ev.data && ev.data.checkout && ev.data.checkout.totalPrice ? ev.data.checkout.totalPrice.amount : null });
        });
      });
    }
  } catch (e) { /* ignore */ }
})();
